import { Injectable, isDevMode } from "@angular/core";
import { Observable } from "rxjs";
import { BaseHttpService } from "./abstracts/http.service";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "@apps/patient-core/src/app/environments/environment";
import { BaseAuthService } from "./abstracts/auth.service";

export enum GRAPHQL_OPERATION_NAMES {
  ADDRESS_AUTOCOMPLETE = "addressAutocomplete",
  ADDRESS_SUGGESTIONS = "addressSuggestions",
  APPOINTMENT = "appointment",
  APPOINTMENTS = "appointments",
  AVAILABILITY = "availability",
  COMMON = "common",
  CONCIERGE_AUTHORIZE = "conciergeAuthorize",
  ESTIMATE = "estimate",
  FIND_GP_PRACTICES = "findGpPractices",
  GET_APPOINTMENT_PRICES = "getAppointmentPrices",
  GET_BOOKABLE_APPOINTMENTS = "bookableAppointments",
  KIOSK_CHECK_IN = "kioskCheckIn",
  MEDICAL_HISTORY = "medicalHistory",
  MEDICAL_HISTORY_HAS_YES_ANSWERS = "medicalHistoryHasYesAnswers",
  MEDICAL_HISTORY_QUESTIONS = "medicalHistoryQuestions",
  MEDICAL_HISTORY_UPDATED_AT = "medicalHistoryUpdatedAt",
  NHS_PR = "nhsPr",
  PATIENT = "patient",
  PATIENT_ACTIONS = "patientActions",
  PATIENT_DEPOSIT = "patientDeposit",
  RECALL_APPOINTMENT_LINK = "recallAppointmentLink",
  SHORT_CODE = "shortCode",
  URL_SITE_ALIAS = "urlSiteAlias",
  VERIFY_PATIENT_DETAILS = "verifyPatientDetails",
  VISIT = "visit",
}

@Injectable({
  providedIn: "root",
})
export class HttpService extends BaseHttpService {
  public constructor(http: HttpClient, authService: BaseAuthService) {
    super(http, authService);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public get(req: { url: string; params?: Params; expires?: number; force?: boolean; uri?: string }): Observable<any> {
    if (req.params && req.params.cache_bust) {
      req.params.cache_bust = this._cacheBustId();
    }

    if (!isDevMode()) {
      if (!req.params) {
        req.params = {};
      }
      req.params.cache_bust = this._cacheBustId();
    }

    if (req.force) {
      if (!req.params) {
        req.params = {};
      }
      req.params.cache_bust = this._cacheBustId();
    }
    req.url = environment.REST_LEGACY_URL + req.url;
    return this.send(req.url, { method: "GET", params: req.params });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public head(url: string): Observable<any> {
    return this.send(url, { method: "HEAD" });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public options(url: string): Observable<any> {
    url = environment.REST_LEGACY_URL + url;
    return this.send(url, { method: "OPTIONS" });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public optionsLegacy(url: string): Observable<any> {
    url = `${environment.REST_LEGACY_URL}/v1/${url}`;
    return this.send(url, { method: "OPTIONS" });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public delete(url: string, body: any): Observable<any> {
    url = environment.REST_LEGACY_URL + url;
    return this.send(url, { params: body, method: "DELETE" });
  }
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public deleteLegacy(url: string, body: any): Observable<any> {
    url = `${environment.REST_LEGACY_URL}/v1/${url}`;
    return this.send(url, { body, method: "DELETE" });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public post(url: string, body: any): Observable<any> {
    url = environment.REST_LEGACY_URL + url;
    return this.send(url, { body, method: "POST" });
  }

  // Used for GraphQL calls
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents, @typescript-eslint/no-explicit-any
  public query<T>(operationName: GRAPHQL_OPERATION_NAMES, query: string, options: any = {}): Observable<T | any> {
    const namedQuery = `query ${operationName} ${query}`;
    return this.send<T>(this._graphUrl, {
      params: {
        operationName,
      },
      body: {
        query: namedQuery,
        operationName,
      },
      method: "POST",
      ...options,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents, @typescript-eslint/no-explicit-any
  public mutation<T = any, R = { data: T; errors?: { message: string; extensions: { code: string } }[] }>(
    operationName: string,
    query: string,
    options: {
      headers?: HttpHeaders | Record<string, string | string[]>;
      withCredentials?: boolean;
    } = {}
  ): Observable<R> {
    const namedMutation = `mutation ${operationName} ${query}`;
    return this.send<R>(this._graphUrl, {
      params: {
        operationName,
      },
      body: {
        query: namedMutation,
        operationName,
      },
      method: "POST",
      ...options,
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public postLegacy(url: string, body: any): Observable<any> {
    url = `${environment.REST_LEGACY_URL}/v1/${url}`;
    return this.send(url, { body, method: "POST" });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public put(url: string, body: any): Observable<any> {
    return this.send(url, { body, method: "PUT" });
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public patch(url: string, body: any): Observable<any> {
    return this.send(url, { body, method: "PATCH" });
  }

  private _cacheBustId() {
    let text = "";
    const possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

    for (let i = 0; i < 5; i++) {
      text += possible.charAt(Math.floor(Math.random() * possible.length));
    }
    return text;
  }

  private get _graphUrl() {
    return environment.GRAPH_URL;
  }
}

/**
 * Define type for valid parameters
 */
// eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents, @typescript-eslint/no-explicit-any
export type Params = string | URLSearchParams | any | { [key: string]: any | any[] };
